import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Button, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  TextField, Select, MenuItem, FormControl, InputLabel, Chip, InputAdornment,
  Alert, Divider, Stack, FormGroup, FormControlLabel, Switch, Tab, Tabs,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  ToggleButton, ToggleButtonGroup, Paper, Autocomplete,
} from '@mui/material';
import {
  Assignment, PictureAsPdf, Save, FolderOpen, Business, EuroSymbol,
  SelectAll, CheckBoxOutlineBlank, Info, LocationOn, LocationCity,
  Delete, Edit, Visibility, CheckCircle, Schedule, PendingActions,
  Link as LinkIcon, FormatListNumbered, Calculate, Search,
} from '@mui/icons-material';
import { measuresAPI } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getMeasureBeschreibung } from '../data/treeFieldOptions';
import EmptyState from '../components/EmptyState';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Measure {
  id: string;
  measure_type: string;
  description: string;
  planned_date: string;
  completed_date?: string;
  status: string;
  baumnummer: string;
  baumart: string;
  hoehe?: string | number;
  stdm?: string | number;
  kdm?: string | number;
  ort: string;
  strasse: string;
}

interface AngebotData {
  bezeichnung: string;
  firma: string;
  ansprechpartner: string;
  zeitraumVon: string;
  zeitraumBis: string;
  bemerkungen: string;
  status: 'offen' | 'beauftragt' | 'abgeschlossen';
  kostenModus: 'einzeln' | 'gesamt';
  gesamtkostenManuell: string;
  buendeln: boolean;
  standardtexte: boolean;
  auftragArt: 'firma' | 'bauhof';
  bauhofMitarbeiter: string;
  stundenlohn: string;
  stunden: string;
}

interface SavedLV {
  id: string;
  bezeichnung: string;
  ort: string;
  firma: string;
  ansprechpartner: string;
  zeitraum_von: string;
  zeitraum_bis: string;
  bemerkungen: string;
  status: string;
  kosten_modus: string;
  gesamtkosten: number;
  strassen: string[];
  anzahl_positionen: number;
  created_at: string;
  created_by_name: string;
  auftrag_art?: string;
  bauhof_mitarbeiter?: string;
  stundenlohn?: number;
  stunden?: number;
}

interface SavedLVDetail extends SavedLV {
  positionen: any[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtEur = (n: number) =>
  n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const fmtDate = (d: string) =>
  d ? new Date(d).toLocaleDateString('de-DE') : '–';

const statusConfig: Record<string, { label: string; color: string }> = {
  offen:         { label: 'Offen',         color: '#ed6c02' },
  beauftragt:    { label: 'Beauftragt',    color: '#1565c0' },
  abgeschlossen: { label: 'Abgeschlossen', color: '#2e7d32' },
};

const measureStatusBadge = (s: string) => {
  const map: Record<string, { label: string; color: string }> = {
    geplant:       { label: 'Geplant',       color: '#ed6c02' },
    abgeschlossen: { label: 'Erledigt',      color: '#2e7d32' },
    offen:         { label: 'Offen',         color: '#9c27b0' },
  };
  const cfg = map[s] || { label: s, color: '#666' };
  return <Chip label={cfg.label} size="small"
    sx={{ fontSize: '0.65rem', height: 18, bgcolor: cfg.color + '20', color: cfg.color, fontWeight: 700 }} />;
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function Leistungsverzeichnis() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState(0);
  const [measures, setMeasures] = useState<Measure[]>([]);
  const [savedLVs, setSavedLVs] = useState<SavedLV[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [detailLV, setDetailLV] = useState<SavedLVDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Filter state
  const [selectedOrt, setSelectedOrt] = useState('');
  const [selectedStrassen, setSelectedStrassen] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [kostenMap, setKostenMap] = useState<Record<string, string>>({});
  const [treePriceMap, setTreePriceMap] = useState<Record<string, string>>({}); // Preis je Baum (Bündelung)
  const [measureSearch, setMeasureSearch] = useState(''); // Suchmaske Maßnahmen-Auswahl
  const [selectableUsers, setSelectableUsers] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    api.get('/users/selectable').then(res => setSelectableUsers(res.data)).catch(() => setSelectableUsers([]));
  }, []);

  const [angebot, setAngebot] = useState<AngebotData>({
    bezeichnung: '', firma: '', ansprechpartner: '',
    zeitraumVon: '', zeitraumBis: '', bemerkungen: '',
    status: 'offen', kostenModus: 'einzeln', gesamtkostenManuell: '', buendeln: false, standardtexte: false,
    auftragArt: 'firma', bauhofMitarbeiter: '', stundenlohn: '', stunden: '',
  });

  // Wenn aus dem Baumblatt zurückgesprungen wurde: LV-Detail wieder öffnen
  useEffect(() => {
    const reopenLvId = (location.state as any)?.reopenLvId;
    if (!reopenLvId || loading) return;
    setTab(1);
    api.get(`/lv/${reopenLvId}`)
      .then((res) => {
        setDetailLV(res.data);
        setDetailOpen(true);
      })
      .catch(() => {/* still still */})
      .finally(() => {
        // State leeren, damit beim nächsten Re-Render der Dialog nicht erneut aufgeht
        navigate(location.pathname, { replace: true, state: {} });
      });
  }, [loading]);  // eslint-disable-line

  useEffect(() => {
    Promise.all([measuresAPI.getAll(), api.get('/lv')])
      .then(([mRes, lvRes]) => { setMeasures(mRes.data); setSavedLVs(lvRes.data); })
      .catch(() => setError('Daten konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  const reloadLVs = () => api.get('/lv').then((r) => setSavedLVs(r.data));

  // ── Derived ───────────────────────────────────────────────────────────────

  const ortList = useMemo(() => {
    const set = new Set<string>();
    measures.forEach((m) => { if (m.ort) set.add(m.ort); });
    return Array.from(set).sort();
  }, [measures]);

  const strassenImOrt = useMemo(() => {
    if (!selectedOrt) return [];
    const set = new Set<string>();
    measures.filter((m) => m.ort === selectedOrt).forEach((m) => { if (m.strasse) set.add(m.strasse); });
    return Array.from(set).sort();
  }, [measures, selectedOrt]);

  const filteredMeasures = useMemo(() => {
    if (!selectedOrt) return [];
    return measures.filter((m) => {
      if (m.ort !== selectedOrt) return false;
      if (selectedStrassen.size > 0 && !selectedStrassen.has(m.strasse)) return false;
      return true;
    });
  }, [measures, selectedOrt, selectedStrassen]);

  const selectedMeasures = filteredMeasures.filter((m) => selectedIds.has(m.id));
  const allMeasuresSelected = filteredMeasures.length > 0 && filteredMeasures.every((m) => selectedIds.has(m.id));

  // Bündelung: ausgewählte Maßnahmen nach Baum (Baumnummer) gruppieren
  const selectedTrees = (() => {
    const map = new Map<string, { baumnummer: string; baumart: string; strasse: string; ort: string; measures: any[] }>();
    for (const m of selectedMeasures) {
      const key = m.baumnummer;
      if (!map.has(key)) map.set(key, { baumnummer: m.baumnummer, baumart: m.baumart, strasse: m.strasse, ort: m.ort, measures: [] });
      map.get(key)!.measures.push(m);
    }
    return Array.from(map.values());
  })();

  // Sortierung der Auswahl-Tabelle (nur Anzeige – Auswahl-Logik bleibt unberührt)
  const [sortBy, setSortBy] = useState<string>('baumnummer');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const handleSort = (col: string) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  };
  const sortedMeasures = useMemo(() => {
    const arr = [...filteredMeasures];
    arr.sort((a: any, b: any) => {
      const cmp = String(a[sortBy] ?? '').localeCompare(
        String(b[sortBy] ?? ''), 'de', { numeric: true, sensitivity: 'base' }
      );
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filteredMeasures, sortBy, sortDir]);

  // Suchmaske: sortierte Liste zusätzlich nach Suchbegriff filtern (nur Anzeige)
  const displayMeasures = useMemo(() => {
    const q = measureSearch.trim().toLowerCase();
    if (!q) return sortedMeasures;
    return sortedMeasures.filter((m: any) =>
      [m.baumnummer, m.baumart, m.strasse, m.measure_type]
        .some((v) => String(v ?? '').toLowerCase().includes(q))
    );
  }, [sortedMeasures, measureSearch]);
  const allStrassenSelected = strassenImOrt.length > 0 && selectedStrassen.size === strassenImOrt.length;

  const berechneteGesamtkosten = angebot.buendeln
    ? selectedTrees.reduce((sum, t) => {
        const k = parseFloat(treePriceMap[t.baumnummer] ?? '');
        return sum + (isNaN(k) ? 0 : k);
      }, 0)
    : selectedMeasures.reduce((sum, m) => {
        const k = parseFloat(kostenMap[m.id] ?? '');
        return sum + (isNaN(k) ? 0 : k);
      }, 0);

  const anzeigeGesamtkosten = angebot.auftragArt === 'bauhof'
    ? (parseFloat(angebot.stundenlohn) || 0) * (parseFloat(angebot.stunden) || 0)
    : angebot.kostenModus === 'gesamt'
    ? (parseFloat(angebot.gesamtkostenManuell) || 0)
    : berechneteGesamtkosten;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const resetEditor = () => {
    setEditingId(null);
    setAngebot({
      bezeichnung: '', firma: '', ansprechpartner: '',
      zeitraumVon: '', zeitraumBis: '', bemerkungen: '',
      status: 'offen', kostenModus: 'einzeln', gesamtkostenManuell: '', buendeln: false, standardtexte: false,
      auftragArt: 'firma', bauhofMitarbeiter: '', stundenlohn: '', stunden: '',
    });
    setSelectedOrt(''); setSelectedStrassen(new Set()); setSelectedIds(new Set()); setKostenMap({}); setTreePriceMap({});
  };

  const handleOrtChange = (val: string) => {
    setSelectedOrt(val); setSelectedStrassen(new Set()); setSelectedIds(new Set()); setKostenMap({}); setTreePriceMap({});
  };

  const toggleStrasse = (s: string) => {
    const next = new Set(selectedStrassen);
    next.has(s) ? next.delete(s) : next.add(s);
    setSelectedStrassen(next); setSelectedIds(new Set()); setKostenMap({}); setTreePriceMap({});
  };

  const toggleAllStrassen = () => {
    setSelectedStrassen(allStrassenSelected ? new Set() : new Set(strassenImOrt));
    setSelectedIds(new Set()); setKostenMap({}); setTreePriceMap({});
  };

  const toggleAllMeasures = () =>
    setSelectedIds(allMeasuresSelected ? new Set() : new Set(filteredMeasures.map((m) => m.id)));

  const toggleOneMeasure = (id: string) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!angebot.bezeichnung) { setError('Bitte eine Bezeichnung eingeben.'); return; }
    if (selectedMeasures.length === 0) { setError('Bitte mindestens eine Maßnahme auswählen.'); return; }
    setError(''); setSaving(true);

    const payload = {
      bezeichnung: angebot.bezeichnung, ort: selectedOrt,
      strassen: Array.from(selectedStrassen),
      firma: angebot.firma, ansprechpartner: angebot.ansprechpartner,
      auftrag_art: angebot.auftragArt,
      bauhof_mitarbeiter: angebot.auftragArt === 'bauhof' ? angebot.bauhofMitarbeiter : null,
      stundenlohn: angebot.auftragArt === 'bauhof' && angebot.stundenlohn.trim() !== '' ? (parseFloat(angebot.stundenlohn) || 0) : null,
      stunden: angebot.auftragArt === 'bauhof' && angebot.stunden.trim() !== '' ? (parseFloat(angebot.stunden) || 0) : null,
      zeitraum_von: angebot.zeitraumVon || null,
      zeitraum_bis: angebot.zeitraumBis || null,
      bemerkungen: angebot.bemerkungen, status: angebot.status,
      kosten_modus: angebot.kostenModus,
      gesamtkosten_manuell: angebot.kostenModus === 'gesamt'
        ? (angebot.gesamtkostenManuell.trim() !== '' ? (parseFloat(angebot.gesamtkostenManuell) || 0) : null)
        : null,
      positionen: angebot.buendeln
        ? selectedTrees.map((t) => {
            const typen = t.measures.map((m: any) => m.measure_type).filter((v: any, i: number, a: any) => v && a.indexOf(v) === i);
            const beschreibung = t.measures
              .map((m: any) => {
                const txt = angebot.standardtexte ? getMeasureBeschreibung(m.measure_type) : m.description;
                return `${m.measure_type}${txt ? ': ' + txt : ''}`;
              })
              .join('\n');
            const preis = treePriceMap[t.baumnummer];
            return {
              measure_id: t.measures[0].id,
              measure_ids: t.measures.map((m: any) => m.id),
              baumnummer: t.baumnummer, baumart: t.baumart,
              ort: t.ort, strasse: t.strasse,
              measure_type: typen.join(' + '),
              description: beschreibung,
              planned_date: t.measures[0].planned_date,
              kosten: angebot.kostenModus === 'einzeln' && preis ? parseFloat(preis) : null,
            };
          })
        : selectedMeasures.map((m) => ({
            measure_id: m.id, measure_ids: [m.id], baumnummer: m.baumnummer, baumart: m.baumart,
            ort: m.ort, strasse: m.strasse, measure_type: m.measure_type,
            description: angebot.standardtexte ? getMeasureBeschreibung(m.measure_type) : m.description,
            planned_date: m.planned_date,
            kosten: angebot.kostenModus === 'einzeln' && kostenMap[m.id]
              ? parseFloat(kostenMap[m.id]) : null,
          })),
    };

    try {
      if (editingId) {
        await api.put(`/lv/${editingId}`, payload);
        setSaveSuccess('Leistungsverzeichnis aktualisiert!');
      } else {
        await api.post('/lv', payload);
        setSaveSuccess('Leistungsverzeichnis gespeichert!');
      }
      await reloadLVs();
      setTimeout(() => setSaveSuccess(''), 4000);
    } catch { setError('Speichern fehlgeschlagen.'); }
    finally { setSaving(false); }
  };

  // ── Load for editing ──────────────────────────────────────────────────────

  const handleEdit = async (lv: SavedLV) => {
    try {
      const res = await api.get(`/lv/${lv.id}`);
      const d: SavedLVDetail = res.data;
      setSelectedOrt(d.ort || '');
      setSelectedStrassen(new Set(d.strassen || []));
      setAngebot({
        bezeichnung: d.bezeichnung, firma: d.firma || '',
        ansprechpartner: d.ansprechpartner || '',
        zeitraumVon: d.zeitraum_von ? d.zeitraum_von.slice(0, 10) : '',
        zeitraumBis: d.zeitraum_bis ? d.zeitraum_bis.slice(0, 10) : '',
        bemerkungen: d.bemerkungen || '', status: d.status as any,
        kostenModus: (d.kosten_modus as any) || 'einzeln',
        gesamtkostenManuell: d.kosten_modus === 'gesamt' && d.gesamtkosten ? String(d.gesamtkosten) : '',
        buendeln: (d.positionen || []).some((p: any) => Array.isArray(p.measure_ids) && p.measure_ids.length > 1),
        standardtexte: false,
        auftragArt: (d.auftrag_art === 'bauhof' ? 'bauhof' : 'firma'),
        bauhofMitarbeiter: d.bauhof_mitarbeiter || '',
        stundenlohn: d.stundenlohn != null ? String(d.stundenlohn) : '',
        stunden: d.stunden != null ? String(d.stunden) : '',
      });
      const ids = new Set<string>();
      const kosten: Record<string, string> = {};
      const treePreise: Record<string, string> = {};
      d.positionen.forEach((p: any) => {
        const mids: string[] = (Array.isArray(p.measure_ids) && p.measure_ids.length)
          ? p.measure_ids : (p.measure_id ? [p.measure_id] : []);
        mids.forEach((mid) => ids.add(mid));
        if (p.measure_id && p.kosten) kosten[p.measure_id] = String(p.kosten);
        if (p.baumnummer && p.kosten) treePreise[p.baumnummer] = String(p.kosten);
      });
      setSelectedIds(ids); setKostenMap(kosten); setTreePriceMap(treePreise); setEditingId(d.id); setTab(0);
    } catch { setError('LV konnte nicht geladen werden.'); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Leistungsverzeichnis wirklich löschen?')) return;
    try { await api.delete(`/lv/${id}`); await reloadLVs(); }
    catch { setError('Löschen fehlgeschlagen.'); }
  };

  const handleView = async (lv: SavedLV) => {
    try { const res = await api.get(`/lv/${lv.id}`); setDetailLV(res.data); setDetailOpen(true); }
    catch { setError('Details konnten nicht geladen werden.'); }
  };

  const handleStatusChange = async (lv: SavedLV, newStatus: string) => {
    const confirmed = newStatus === 'abgeschlossen'
      ? window.confirm(
          `LV auf "Abgeschlossen" setzen?\n\nDadurch werden alle verknüpften Maßnahmen (${lv.anzahl_positionen} Stück) automatisch als "abgeschlossen" markiert.`
        )
      : true;
    if (!confirmed) return;
    try {
      await api.put(`/lv/${lv.id}`, { ...lv, status: newStatus, strassen: lv.strassen || [] });
      await reloadLVs();
      if (newStatus === 'abgeschlossen') {
        setSaveSuccess(`${lv.anzahl_positionen} Maßnahmen wurden automatisch abgeschlossen.`);
        setTimeout(() => setSaveSuccess(''), 4000);
      }
    } catch { setError('Status-Änderung fehlgeschlagen.'); }
  };

  // ── PDF Export ────────────────────────────────────────────────────────────

  const [pdfKopf, setPdfKopf] = useState<{ logo?: string | null; gemeinde_name?: string | null; gemeinde_adresse?: string | null } | null>(null);
  useEffect(() => {
    api.get('/settings/pdf-kopf').then(r => setPdfKopf(r.data)).catch(() => setPdfKopf(null));
  }, []);

  const exportPDF = (data?: SavedLVDetail) => {
    const mList = data ? data.positionen : selectedMeasures.map((m) => ({
      baumnummer: m.baumnummer, baumart: m.baumart, strasse: m.strasse,
      measure_type: m.measure_type, description: m.description, planned_date: m.planned_date,
      kosten: kostenMap[m.id] ? parseFloat(kostenMap[m.id]) : null,
    }));
    const meta = {
      bezeichnung: data?.bezeichnung || angebot.bezeichnung,
      ort: data?.ort || selectedOrt, firma: data?.firma || angebot.firma,
      ansprechpartner: data?.ansprechpartner || angebot.ansprechpartner,
      zeitraum_von: data?.zeitraum_von || angebot.zeitraumVon,
      zeitraum_bis: data?.zeitraum_bis || angebot.zeitraumBis,
      bemerkungen: data?.bemerkungen || angebot.bemerkungen,
      kosten_modus: data?.kosten_modus || angebot.kostenModus,
      auftrag_art: (data ? (data as any).auftrag_art : angebot.auftragArt) || 'firma',
      bauhof_mitarbeiter: (data ? (data as any).bauhof_mitarbeiter : angebot.bauhofMitarbeiter) || '',
      stundenlohn: data ? (data as any).stundenlohn : (parseFloat(angebot.stundenlohn) || 0),
      stunden: data ? (data as any).stunden : (parseFloat(angebot.stunden) || 0),
    };
    const total = data
      ? parseFloat(String(data.gesamtkosten || 0))
      : anzeigeGesamtkosten;
    const isGesamt = meta.kosten_modus === 'gesamt';

    const rows = mList.map((p: any, i: number) => {
      const masse = [p.hoehe && `H ${p.hoehe} m`, p.stdm && `Ø ${p.stdm} cm`, p.kdm && `K ${p.kdm} m`].filter(Boolean).join(' · ');
      return `
      <tr><td class="center">${i+1}</td>
      <td class="center"><strong>${p.baumnummer??'–'}</strong></td>
      <td><em>${p.baumart??'–'}</em></td>
      <td class="masse">${masse||'–'}</td>
      <td>${p.strasse??'–'}</td>
      <td>${p.measure_type??'–'}</td>
      <td class="desc">${p.description??'–'}</td>
      <td class="center">${p.planned_date?fmtDate(p.planned_date):'–'}</td>
      <td class="right">${!isGesamt&&p.kosten?fmtEur(parseFloat(p.kosten)):'–'}</td></tr>`;
    }).join('');

    const kopf = pdfKopf || {};
    const logoHtml = kopf.logo ? `<img src="${kopf.logo}" alt="Logo" />` : '';
    const absenderHtml = (kopf.gemeinde_name || kopf.gemeinde_adresse)
      ? `<div class="absender"><strong>${kopf.gemeinde_name || ''}</strong>${kopf.gemeinde_adresse ? `<br><span>${kopf.gemeinde_adresse}</span>` : ''}</div>`
      : '<div></div>';

    const html = `<!DOCTYPE html><html lang="de"><head><meta charset="UTF-8">
<title>${meta.bezeichnung||'Leistungsverzeichnis'}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:10.5pt;color:#1a1a1a;padding:18mm 20mm;line-height:1.45}
  .letterhead{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px}
  .absender{font-size:8.5pt;color:#555;line-height:1.4} .absender strong{color:#1a1a1a;font-size:10pt}
  .logo img{max-height:66px;max-width:190px;object-fit:contain}
  .titlebar{border-bottom:3px solid #2d5a27;padding-bottom:8px;margin-bottom:14px}
  .titlebar h1{font-size:19pt;font-weight:800;color:#2d5a27} .titlebar p{font-size:9pt;color:#666;margin-top:2px}
  .meta{display:grid;grid-template-columns:1fr 1fr;gap:5px 24px;background:#f6faf5;border:1px solid #c8e0c4;border-radius:6px;padding:10px 14px;margin-bottom:14px;font-size:9.5pt}
  .meta-item{display:flex;gap:6px} .meta-item .label{color:#666;white-space:nowrap} .meta-item .value{font-weight:600}
  table{width:100%;border-collapse:collapse;font-size:9pt}
  thead th{background:#2d5a27;color:white;padding:7px 8px;text-align:left;font-size:8pt;text-transform:uppercase;letter-spacing:.6px;font-weight:700}
  tbody td{padding:6px 8px;border-bottom:1px solid #e8e8e8;vertical-align:top}
  tbody tr:nth-child(even) td{background:#f9fdf8}
  .desc{font-size:8.5pt;color:#444;max-width:180px} .masse{font-size:8pt;color:#555;white-space:nowrap} .center{text-align:center} .right{text-align:right;font-weight:600}
  .totals{display:flex;justify-content:flex-end;margin-top:10px}
  .totals-box{background:#2d5a27;color:white;padding:8px 16px;border-radius:6px;display:flex;gap:20px;align-items:center}
  .totals-box .label{font-size:9pt;opacity:.85} .totals-box .amount{font-size:14pt;font-weight:800}
  .remarks{margin-top:14px;border-left:3px solid #2d5a27;padding:6px 10px;background:#f9fdf8}
  .signatures{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:28px}
  .sign-line{border-top:1.5px solid #888;margin-top:36px;padding-top:5px;font-size:8.5pt;color:#666}
  @media print{body{padding:10mm 14mm}}
</style></head><body>
  <div class="letterhead">
    ${absenderHtml}
    <div class="logo">${logoHtml}</div>
  </div>
  <div class="titlebar">
    <h1>${meta.bezeichnung||'Leistungsverzeichnis'}</h1>
    <p>Leistungsverzeichnis · erstellt am ${new Date().toLocaleDateString('de-DE',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</p>
  </div>
  <div class="meta">
    <div class="meta-item"><span class="label">Ort:</span><span class="value">${meta.ort||'–'}</span></div>
    <div class="meta-item"><span class="label">${meta.auftrag_art==='bauhof'?'Ausführung:':'Firma:'}</span><span class="value">${meta.auftrag_art==='bauhof'?('Bauhof (intern)'+(meta.bauhof_mitarbeiter?' – '+meta.bauhof_mitarbeiter:'')):(meta.firma||'–')}</span></div>
    <div class="meta-item"><span class="label">Zeitraum:</span><span class="value">${meta.zeitraum_von&&meta.zeitraum_bis?`${fmtDate(meta.zeitraum_von)} – ${fmtDate(meta.zeitraum_bis)}`:'–'}</span></div>
    <div class="meta-item"><span class="label">${meta.auftrag_art==='bauhof'?'Stundensatz:':'Ansprechpartner:'}</span><span class="value">${meta.auftrag_art==='bauhof'?(`${(parseFloat(String(meta.stundenlohn))||0).toLocaleString('de-DE')} €/h × ${(parseFloat(String(meta.stunden))||0).toLocaleString('de-DE')} h`):(meta.ansprechpartner||'–')}</span></div>
    <div class="meta-item"><span class="label">Positionen:</span><span class="value">${mList.length}</span></div>
    <div class="meta-item"><span class="label">Kostenerfassung:</span><span class="value">${isGesamt?'Gesamtbetrag':'Pro Position'}</span></div>
  </div>
  <table><thead><tr>
    <th style="width:28px">Pos.</th><th style="width:55px">Baum-Nr.</th>
    <th style="width:80px">Baumart</th><th style="width:88px">Maße</th><th style="width:100px">Straße</th>
    <th style="width:100px">Maßnahme</th><th>Beschreibung</th>
    <th style="width:70px">Geplant</th>
    <th style="width:80px;text-align:right">${isGesamt?'–':'Kosten'}</th>
  </tr></thead><tbody>${rows}</tbody></table>
  <div class="totals"><div class="totals-box">
    <span class="label">Gesamtkosten (netto)${isGesamt?' – Pauschalbetrag':''}</span>
    <span class="amount">${fmtEur(total)}</span>
  </div></div>
  ${meta.bemerkungen?`<div class="remarks"><strong>Bemerkungen:</strong> ${meta.bemerkungen}</div>`:''}
  <div class="signatures">
    <div><div class="sign-line">Ort, Datum · Auftraggeber</div></div>
    <div><div class="sign-line">Ort, Datum · ${meta.firma||'Auftragnehmer'}</div></div>
  </div>
</body></html>`;
    const w = window.open('','_blank');
    if (!w) { alert('Bitte Popup-Blocker deaktivieren.'); return; }
    w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 600);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const canSave = selectedMeasures.length > 0 && !!angebot.bezeichnung;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Assignment sx={{ fontSize: 28, color: '#4a7c59' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Leistungsverzeichnis</Typography>
          <Typography variant="body2" color="text.secondary">Maßnahmen auswählen, speichern und exportieren</Typography>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ '& .MuiTab-root.Mui-selected': { color: '#4a7c59' }, '& .MuiTabs-indicator': { bgcolor: '#4a7c59' } }}>
          <Tab label={editingId ? '✏️ LV bearbeiten' : '➕ Neues LV'} />
          <Tab label={`📁 Gespeicherte LVs (${savedLVs.length})`} />
        </Tabs>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>{saveSuccess}</Alert>}

      {/* ── TAB 0: LV erstellen / bearbeiten ── */}
      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>

            {/* Bearbeitungs-Hinweis – nur im Bearbeiten-Modus */}
            {editingId && (
              <Alert
                severity="info"
                icon={<Edit fontSize="inherit" />}
                sx={{ mb: 2, border: '1px solid', borderColor: 'info.light', '& .MuiAlert-message': { width: '100%' } }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.3 }}>
                  Bearbeitungsmodus{angebot.bezeichnung ? `: „${angebot.bezeichnung}“` : ''}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                  Du bearbeitest ein vorhandenes Leistungsverzeichnis. Stammdaten, Ort/Straßen und Maßnahmen lassen sich anpassen –
                  in <strong>Schritt&nbsp;3</strong> über die Häkchen Bäume bzw. Maßnahmen <strong>hinzufügen oder abwählen</strong>.
                  Zum Übernehmen unten auf <strong>„Aktualisieren“</strong> klicken. Mit <strong>„Bearbeitung abbrechen“</strong> verwirfst du die Änderungen.
                </Typography>
              </Alert>
            )}

            {/* Ort */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <LocationCity sx={{ color: '#4a7c59', fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight={700}>1. Ort auswählen</Typography>
                </Box>
                <FormControl fullWidth size="small">
                  <InputLabel>Ort / Gemeinde</InputLabel>
                  <Select value={selectedOrt} label="Ort / Gemeinde"
                    onChange={(e) => handleOrtChange(e.target.value)} disabled={loading}>
                    {ortList.map((ort) => (
                      <MenuItem key={ort} value={ort}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                          <span>{ort}</span>
                          <Chip label={measures.filter(m => m.ort === ort).length} size="small"
                            sx={{ fontSize: '0.7rem', bgcolor: '#4a7c5915', color: '#4a7c59' }} />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>

            {/* Straßen */}
            {selectedOrt && strassenImOrt.length > 0 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn sx={{ color: '#4a7c59', fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight={700}>2. Straßen</Typography>
                      <Chip label="optional" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    </Box>
                    <Button size="small" startIcon={allStrassenSelected ? <CheckBoxOutlineBlank /> : <SelectAll />}
                      onClick={toggleAllStrassen} sx={{ color: '#4a7c59' }}>
                      {allStrassenSelected ? 'Alle abwählen' : 'Alle auswählen'}
                    </Button>
                  </Box>
                  <FormGroup sx={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                    {strassenImOrt.map((s) => {
                      const count = measures.filter(m => m.ort === selectedOrt && m.strasse === s).length;
                      return (
                        <FormControlLabel key={s} sx={{ width: { xs: '100%', sm: '50%' }, mr: 0, pr: 1 }}
                          control={<Checkbox checked={selectedStrassen.has(s)} onChange={() => toggleStrasse(s)}
                            size="small" sx={{ '&.Mui-checked': { color: '#4a7c59' } }} />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{s}</Typography>
                              <Chip label={count} size="small" sx={{ fontSize: '0.7rem', height: 18, bgcolor: '#4a7c5915', color: '#4a7c59' }} />
                            </Box>
                          } />
                      );
                    })}
                  </FormGroup>
                  {selectedStrassen.size > 0 && (
                    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.from(selectedStrassen).map((s) => (
                        <Chip key={s} label={s} size="small" onDelete={() => toggleStrasse(s)}
                          sx={{ bgcolor: '#4a7c59', color: 'white', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }} />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Maßnahmen */}
            <Card sx={{ minHeight: 500, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ pb: '12px !important', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>3. Maßnahmen auswählen</Typography>
                    {selectedIds.size > 0 && <Chip label={`${selectedIds.size} gewählt`} size="small" sx={{ bgcolor: '#4a7c59', color: 'white' }} />}
                  </Box>
                  {filteredMeasures.length > 0 && (
                    <Button size="small" startIcon={allMeasuresSelected ? <CheckBoxOutlineBlank /> : <SelectAll />}
                      onClick={toggleAllMeasures} sx={{ color: '#4a7c59' }}>
                      {allMeasuresSelected ? 'Alle abwählen' : 'Alle auswählen'}
                    </Button>
                  )}
                </Box>

                {selectedOrt && filteredMeasures.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.7, mb: 1, px: 0.5 }}>
                    <Info sx={{ fontSize: 15, color: 'text.disabled', mt: '1px' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                      Häkchen setzen fügt den Baum samt Maßnahme zum Leistungsverzeichnis hinzu, Häkchen entfernen nimmt ihn wieder heraus.
                      {editingId ? ' Deine Änderungen werden erst mit „Aktualisieren“ gespeichert.' : ''}
                    </Typography>
                  </Box>
                )}

                {selectedOrt && filteredMeasures.length > 0 && (
                  <TextField size="small" fullWidth placeholder="Suche: Baum-Nr., Baumart, Straße, Maßnahme…"
                    value={measureSearch} onChange={(e) => setMeasureSearch(e.target.value)}
                    sx={{ mb: 1 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
                  />
                )}

                {!selectedOrt ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', py: 4 }}>
                    <Info sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2">Bitte zuerst einen Ort auswählen</Typography>
                  </Box>
                ) : filteredMeasures.length === 0 ? (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Alert severity="info" sx={{ width: '100%' }}>Keine Maßnahmen für diese Auswahl.</Alert>
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 600, overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: '#4a7c59', color: 'white', fontWeight: 700, fontSize: '0.75rem' } }}>
                          <TableCell padding="checkbox" sx={{ bgcolor: '#4a7c59 !important' }}>
                            <Checkbox checked={allMeasuresSelected} indeterminate={selectedIds.size > 0 && !allMeasuresSelected}
                              onChange={toggleAllMeasures} size="small"
                              sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }} />
                          </TableCell>
                          {([
                            ['baumnummer', 'Baum-Nr.'],
                            ['baumart', 'Baumart'],
                            ['hoehe', 'Maße'],
                            ['strasse', 'Straße'],
                            ['measure_type', 'Maßnahme'],
                            ['status', 'Status'],
                            ['planned_date', 'Geplant'],
                            ['completed_date', 'Erledigt'],
                          ] as [string, string][]).map(([col, label]) => (
                            <TableCell key={col} sortDirection={sortBy === col ? sortDir : false}>
                              <TableSortLabel
                                active={sortBy === col}
                                direction={sortBy === col ? sortDir : 'asc'}
                                onClick={() => handleSort(col)}
                                sx={{ color: 'white !important', '&.Mui-active': { color: 'white !important' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                              >
                                {label}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                          {angebot.kostenModus === 'einzeln' && !angebot.buendeln && <TableCell>Kosten (€)</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayMeasures.map((m) => {
                          const checked = selectedIds.has(m.id);
                          return (
                            <TableRow key={m.id} hover selected={checked}
                              sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: '#4a7c5910' } }}
                              onClick={() => toggleOneMeasure(m.id)}>
                              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                <Checkbox checked={checked} onChange={() => toggleOneMeasure(m.id)} size="small"
                                  sx={{ '&.Mui-checked': { color: '#4a7c59' } }} />
                              </TableCell>
                              <TableCell><Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>#{m.baumnummer}</Typography></TableCell>
                              <TableCell sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>{m.baumart||'–'}</TableCell>
                              <TableCell sx={{ fontSize: '0.72rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                {(m.hoehe || m.stdm || m.kdm)
                                  ? [m.hoehe && `H ${m.hoehe} m`, m.stdm && `Ø ${m.stdm} cm`, m.kdm && `K ${m.kdm} m`].filter(Boolean).join(' · ')
                                  : '–'}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{m.strasse||'–'}</TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>
                                {m.measure_type||'–'}
                                {m.description && (
                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.3, whiteSpace: 'normal' }}>
                                    {m.description}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{measureStatusBadge(m.status)}</TableCell>
                              <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{m.planned_date?fmtDate(m.planned_date):'–'}</TableCell>
                              <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap', color: m.completed_date ? 'success.main' : 'text.disabled', fontWeight: m.completed_date ? 600 : 400 }}>
                                {m.completed_date?fmtDate(m.completed_date):'–'}
                              </TableCell>
                              {angebot.kostenModus === 'einzeln' && !angebot.buendeln && (
                                <TableCell onClick={(e) => e.stopPropagation()} sx={{ minWidth: 110 }}>
                                  <TextField size="small" value={kostenMap[m.id]??''} type="number"
                                    onChange={(e) => setKostenMap(p => ({...p,[m.id]:e.target.value}))}
                                    placeholder="0,00" disabled={!checked}
                                    inputProps={{ style: { fontSize: '0.82rem', textAlign: 'right' } }} sx={{ width: 100 }} />
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* RIGHT */}
          <Grid item xs={12} md={4}>

            {/* LV Metadaten */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Business sx={{ color: '#4a7c59', fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight={700}>LV-Details</Typography>
                </Box>
                <Stack spacing={1.5}>
                  <TextField label="Bezeichnung *" size="small" fullWidth value={angebot.bezeichnung}
                    placeholder="z.B. Fällungen Frühling 2026"
                    onChange={(e) => setAngebot(p => ({...p, bezeichnung: e.target.value}))} />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={angebot.status} label="Status"
                      onChange={(e) => setAngebot(p => ({...p, status: e.target.value as any}))}>
                      {Object.entries(statusConfig).map(([key, cfg]) => (
                        <MenuItem key={key} value={key}>
                          <Typography sx={{ color: cfg.color, fontWeight: 600, fontSize: '0.85rem' }}>{cfg.label}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Auftrag an</Typography>
                    <ToggleButtonGroup value={angebot.auftragArt} exclusive size="small" fullWidth
                      onChange={(_, v) => { if (v) setAngebot(p => ({ ...p, auftragArt: v })); }}>
                      <ToggleButton value="firma">Firma</ToggleButton>
                      <ToggleButton value="bauhof">Bauhof (intern)</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  {angebot.auftragArt === 'firma' ? (
                    <>
                      <TextField label="Firma" size="small" fullWidth value={angebot.firma}
                        onChange={(e) => setAngebot(p => ({...p, firma: e.target.value}))} />
                      <TextField label="Ansprechpartner" size="small" fullWidth value={angebot.ansprechpartner}
                        onChange={(e) => setAngebot(p => ({...p, ansprechpartner: e.target.value}))} />
                    </>
                  ) : (
                    <>
                      <Autocomplete multiple freeSolo size="small"
                        options={selectableUsers.map(u => u.name)}
                        value={angebot.bauhofMitarbeiter ? angebot.bauhofMitarbeiter.split(',').map(s => s.trim()).filter(Boolean) : []}
                        onChange={(_, val) => setAngebot(p => ({ ...p, bauhofMitarbeiter: (val as string[]).join(', ') }))}
                        renderInput={(params) => <TextField {...params} label="Mitarbeiter / Kolonne" placeholder="Nutzer wählen oder eintippen" />}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField label="Stundenlohn (€/h)" size="small" fullWidth type="number" value={angebot.stundenlohn}
                          onChange={(e) => setAngebot(p => ({...p, stundenlohn: e.target.value}))} />
                        <TextField label="Stunden" size="small" fullWidth type="number" value={angebot.stunden}
                          onChange={(e) => setAngebot(p => ({...p, stunden: e.target.value}))} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'rgba(74,124,89,0.10)', borderRadius: 1.5, px: 1.5, py: 1 }}>
                        <Typography variant="body2" color="text.secondary">Gesamtkosten (Bauhof):</Typography>
                        <Typography variant="body1" fontWeight={800} color="#4a7c59">
                          {fmtEur((parseFloat(angebot.stundenlohn) || 0) * (parseFloat(angebot.stunden) || 0))}
                        </Typography>
                      </Box>
                    </>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Leistungszeitraum</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField type="date" size="small" label="Von" value={angebot.zeitraumVon}
                        onChange={(e) => setAngebot(p => ({...p, zeitraumVon: e.target.value}))}
                        sx={{ flex: 1 }} InputLabelProps={{ shrink: true }} />
                      <Typography variant="caption">–</Typography>
                      <TextField type="date" size="small" label="Bis" value={angebot.zeitraumBis}
                        onChange={(e) => setAngebot(p => ({...p, zeitraumBis: e.target.value}))}
                        sx={{ flex: 1 }} InputLabelProps={{ shrink: true }} />
                    </Box>
                  </Box>
                  <TextField label="Bemerkungen" size="small" fullWidth multiline rows={2}
                    value={angebot.bemerkungen}
                    onChange={(e) => setAngebot(p => ({...p, bemerkungen: e.target.value}))} />
                </Stack>
              </CardContent>
            </Card>

            {/* Kostenerfassung */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <EuroSymbol sx={{ color: '#4a7c59', fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight={700}>Kostenerfassung</Typography>
                </Box>

                {angebot.auftragArt === 'bauhof' ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Abrechnung nach Stundenlohn – siehe „Auftrag an Bauhof" oben.
                  </Typography>
                ) : (
                <>
                {/* Bündeln pro Baum (optional) – hervorgehoben */}
                <Box sx={{
                  mb: 1.5, p: 1.25, borderRadius: 1.5, border: '1px solid',
                  borderColor: angebot.buendeln ? '#4a7c59' : 'divider',
                  bgcolor: angebot.buendeln ? 'rgba(74,124,89,0.10)' : 'action.hover',
                  transition: 'background-color 0.15s, border-color 0.15s',
                }}>
                  <FormControlLabel
                    sx={{ m: 0, alignItems: 'center', width: '100%' }}
                    control={<Switch checked={angebot.buendeln}
                      onChange={(e) => setAngebot(p => ({ ...p, buendeln: e.target.checked }))} />}
                    label={<Box sx={{ ml: 0.5 }}>
                      <Typography variant="body2" fontWeight={700}
                        sx={{ color: angebot.buendeln ? '#4a7c59' : 'text.primary' }}>
                        Maßnahmen pro Baum bündeln
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ein Auftrag &amp; ein Preis je Baum – verhindert doppelte Berechnung.
                      </Typography>
                    </Box>}
                  />
                </Box>

                {/* Standard-Leistungstexte je Maßnahmentyp verwenden */}
                <Box sx={{
                  mb: 1.5, p: 1.25, borderRadius: 1.5, border: '1px solid',
                  borderColor: angebot.standardtexte ? '#4a7c59' : 'divider',
                  bgcolor: angebot.standardtexte ? 'rgba(74,124,89,0.10)' : 'action.hover',
                  transition: 'background-color 0.15s, border-color 0.15s',
                }}>
                  <FormControlLabel
                    sx={{ m: 0, alignItems: 'center', width: '100%' }}
                    control={<Switch checked={angebot.standardtexte}
                      onChange={(e) => setAngebot(p => ({ ...p, standardtexte: e.target.checked }))} />}
                    label={<Box sx={{ ml: 0.5 }}>
                      <Typography variant="body2" fontWeight={700}
                        sx={{ color: angebot.standardtexte ? '#4a7c59' : 'text.primary' }}>
                        Standard-Leistungstexte verwenden
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Nutzt die hinterlegte Standard-Beschreibung je Maßnahmentyp statt der Einzel-Bemerkung.
                      </Typography>
                    </Box>}
                  />
                </Box>

                {/* Modus-Umschalter */}
                <ToggleButtonGroup value={angebot.kostenModus} exclusive size="small" fullWidth
                  onChange={(_, v) => { if (v) setAngebot(p => ({...p, kostenModus: v})); }}
                  sx={{ mb: 1.5 }}>
                  <ToggleButton value="einzeln"
                    sx={{ fontSize: '0.75rem', '&.Mui-selected': { bgcolor: '#4a7c5920', color: '#4a7c59', fontWeight: 700 } }}>
                    <FormatListNumbered sx={{ fontSize: 16, mr: 0.5 }} /> Pro Position
                  </ToggleButton>
                  <ToggleButton value="gesamt"
                    sx={{ fontSize: '0.75rem', '&.Mui-selected': { bgcolor: '#4a7c5920', color: '#4a7c59', fontWeight: 700 } }}>
                    <Calculate sx={{ fontSize: 16, mr: 0.5 }} /> Gesamtbetrag
                  </ToggleButton>
                </ToggleButtonGroup>

                {angebot.kostenModus === 'einzeln' ? (
                  <>
                    {selectedMeasures.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">Noch keine Maßnahmen ausgewählt.</Typography>
                    ) : angebot.buendeln ? (
                      <Stack spacing={0.75} sx={{ maxHeight: 220, overflowY: 'auto', mb: 1 }}>
                        {selectedTrees.map((t, i) => {
                          const typen = t.measures.map((m: any) => m.measure_type).filter((v: any, idx: number, a: any) => v && a.indexOf(v) === idx).join(' + ');
                          return (
                            <Box key={t.baumnummer} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                                {i+1}. #{t.baumnummer} – {typen}
                              </Typography>
                              <TextField size="small" type="number" placeholder="0,00" sx={{ width: 110 }}
                                value={treePriceMap[t.baumnummer] ?? ''}
                                onChange={(e) => setTreePriceMap(p => ({ ...p, [t.baumnummer]: e.target.value }))}
                                inputProps={{ style: { textAlign: 'right' } }} />
                            </Box>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Stack spacing={0.5} sx={{ maxHeight: 160, overflowY: 'auto', mb: 1 }}>
                        {selectedMeasures.map((m, i) => {
                          const k = parseFloat(kostenMap[m.id]??'');
                          return (
                            <Box key={m.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, mr: 1 }}>
                                {i+1}. #{m.baumnummer} – {m.measure_type}
                              </Typography>
                              <Typography variant="caption" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                                {isNaN(k)||kostenMap[m.id]===undefined?'–':fmtEur(k)}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Summe:</Typography>
                      <Typography variant="body1" fontWeight={800} color="#4a7c59">{fmtEur(berechneteGesamtkosten)}</Typography>
                    </Box>
                  </>
                ) : (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Pauschalbetrag für alle {selectedMeasures.length} Maßnahmen
                    </Typography>
                    <TextField
                      label="Gesamtbetrag (€ netto)" size="small" fullWidth type="number"
                      value={angebot.gesamtkostenManuell}
                      onChange={(e) => setAngebot(p => ({...p, gesamtkostenManuell: e.target.value}))}
                      placeholder="0,00"
                      inputProps={{ style: { textAlign: 'right', fontWeight: 700, fontSize: '1rem' } }}
                    />
                    {angebot.gesamtkostenManuell && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Ø {fmtEur((parseFloat(angebot.gesamtkostenManuell)||0) / Math.max(selectedMeasures.length,1))} pro Maßnahme
                      </Typography>
                    )}
                  </Box>
                )}
                </>
                )}

                {/* Gesamtanzeige */}
                <Box sx={{ bgcolor: '#4a7c59', color: 'white', borderRadius: 2, p: 1.5, textAlign: 'center', mt: 1.5, mb: 1.5 }}>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>Gesamtkosten (netto)</Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>{fmtEur(anzeigeGesamtkosten)}</Typography>
                </Box>

                <Stack spacing={1}>
                  <Button fullWidth variant="contained" startIcon={<Save />} onClick={handleSave}
                    disabled={!canSave || saving} size="small"
                    sx={{ bgcolor: '#4a7c59', '&:hover': { bgcolor: '#3a6347' } }}>
                    {saving ? 'Speichern…' : editingId ? 'Aktualisieren' : 'LV speichern'}
                  </Button>
                  <Button fullWidth variant="outlined" startIcon={<PictureAsPdf />}
                    onClick={() => exportPDF()} disabled={selectedMeasures.length === 0} size="small"
                    sx={{ borderColor: '#4a7c59', color: '#4a7c59' }}>
                    Als PDF exportieren
                  </Button>
                </Stack>
                {editingId && (
                  <Button fullWidth size="small" variant="outlined" sx={{ mt: 1, color: 'text.secondary', borderColor: 'divider' }} onClick={resetEditor}>
                    Bearbeitung abbrechen
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Status-Hinweis */}
            {angebot.status === 'abgeschlossen' && selectedMeasures.length > 0 && (
              <Paper sx={{ p: 1.5, bgcolor: '#fff3e0', border: '1px solid #ffcc02' }}>
                <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600 }}>
                  ⚠️ Beim Speichern als „Abgeschlossen" werden {selectedMeasures.length} Maßnahmen automatisch auf erledigt gesetzt.
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      {/* ── TAB 1: Gespeicherte LVs ── */}
      {tab === 1 && (
        <Card>
          <CardContent>
            {savedLVs.length === 0 ? (
              <EmptyState
                icon={FolderOpen}
                title="Noch keine Leistungsverzeichnisse"
                description="Erstelle dein erstes LV, indem du Bäume nach Ort und Straße filterst, dann Maßnahmen und Kosten zuordnest."
                action={{ label: 'Jetzt erstellen', icon: Assignment, onClick: () => setTab(0) }}
                accentColor="primary"
              />
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#4a7c59', color: 'white', fontWeight: 700, fontSize: '0.75rem' } }}>
                      <TableCell>Bezeichnung</TableCell>
                      <TableCell>Ort</TableCell>
                      <TableCell>Firma</TableCell>
                      <TableCell>Zeitraum</TableCell>
                      <TableCell>Pos.</TableCell>
                      <TableCell>Kosten</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Erstellt</TableCell>
                      <TableCell align="right">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {savedLVs.map((lv) => {
                      const cfg = statusConfig[lv.status] || statusConfig.offen;
                      return (
                        <TableRow key={lv.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>{lv.bezeichnung}</Typography>
                            {lv.kosten_modus === 'gesamt' && (
                              <Chip label="Pauschal" size="small" sx={{ fontSize: '0.62rem', height: 16, mt: 0.3 }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{lv.ort||'–'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{lv.firma||'–'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                            {lv.zeitraum_von?fmtDate(lv.zeitraum_von):'–'}
                            {lv.zeitraum_bis?` – ${fmtDate(lv.zeitraum_bis)}`:''}
                          </TableCell>
                          <TableCell>
                            <Chip label={lv.anzahl_positionen} size="small"
                              sx={{ fontSize: '0.7rem', bgcolor: '#4a7c5915', color: '#4a7c59' }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                            {fmtEur(parseFloat(String(lv.gesamtkosten||0)))}
                          </TableCell>
                          <TableCell>
                            <Select value={lv.status} size="small" variant="standard" disableUnderline
                              onChange={(e) => handleStatusChange(lv, e.target.value)}
                              sx={{ fontSize: '0.75rem', color: cfg.color, fontWeight: 600 }}>
                              {Object.entries(statusConfig).map(([key, c]) => (
                                <MenuItem key={key} value={key} sx={{ fontSize: '0.78rem', color: c.color }}>{c.label}</MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            {fmtDate(lv.created_at)}<br />
                            <span style={{ fontSize: '0.7rem' }}>{lv.created_by_name}</span>
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Ansehen"><IconButton size="small" onClick={() => handleView(lv)}><Visibility sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                              <Tooltip title="Bearbeiten"><IconButton size="small" onClick={() => handleEdit(lv)}><Edit sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                              {isAdmin && <Tooltip title="Löschen"><IconButton size="small" color="error" onClick={() => handleDelete(lv.id)}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Detail Dialog ── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        {detailLV && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{detailLV.bezeichnung}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {detailLV.ort} · {detailLV.firma||'Keine Firma'} · {fmtDate(detailLV.created_at)}
                    {detailLV.kosten_modus === 'gesamt' && ' · Pauschalabrechnung'}
                  </Typography>
                </Box>
                <Chip label={statusConfig[detailLV.status]?.label} size="small"
                  sx={{ bgcolor: statusConfig[detailLV.status]?.color+'20', color: statusConfig[detailLV.status]?.color, fontWeight: 700 }} />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#4a7c59', color: 'white', fontWeight: 700, fontSize: '0.75rem' } }}>
                      <TableCell>Pos.</TableCell><TableCell>Baum-Nr.</TableCell>
                      <TableCell>Baumart</TableCell><TableCell>Maße</TableCell><TableCell>Straße</TableCell>
                      <TableCell>Maßnahme</TableCell>
                      <TableCell>Geplant</TableCell>
                      <TableCell>Erledigt</TableCell>
                      <TableCell>Ma.-Status</TableCell>
                      {detailLV.kosten_modus === 'einzeln' && <TableCell align="right">Kosten</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailLV.positionen.map((p: any) => {
                      const treeId = p.measure_tree_id;
                      const goToTree = () => {
                        if (!treeId) return;
                        setDetailOpen(false);
                        navigate(`/trees/${treeId}`, {
                          state: {
                            from: 'lv',
                            lvId: detailLV.id,
                            lvBezeichnung: detailLV.bezeichnung,
                          },
                        });
                      };
                      return (
                      <TableRow key={p.id} hover>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{p.position_nr}</TableCell>
                        <TableCell
                          onClick={goToTree}
                          sx={{
                            fontSize: '0.82rem',
                            fontWeight: 700,
                            cursor: treeId ? 'pointer' : 'default',
                            color: treeId ? 'primary.main' : 'inherit',
                            textDecoration: treeId ? 'underline' : 'none',
                            textUnderlineOffset: '3px',
                            '&:hover': treeId ? { color: 'primary.dark' } : {},
                          }}
                          title={treeId ? 'Zum Baumblatt springen' : ''}
                        >
                          #{p.baumnummer}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', fontStyle: 'italic' }}>{p.baumart}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {(p.hoehe || p.stdm || p.kdm)
                            ? [p.hoehe && `H ${p.hoehe} m`, p.stdm && `Ø ${p.stdm} cm`, p.kdm && `K ${p.kdm} m`].filter(Boolean).join(' · ')
                            : '–'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{p.strasse}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{p.measure_type}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{p.planned_date?fmtDate(p.planned_date):'–'}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: p.measure_completed_date ? 'success.main' : 'text.disabled', fontWeight: p.measure_completed_date ? 600 : 400 }}>
                          {p.measure_completed_date?fmtDate(p.measure_completed_date):'–'}
                        </TableCell>
                        <TableCell>{measureStatusBadge(p.measure_current_status||p.measure_status||'geplant')}</TableCell>
                        {detailLV.kosten_modus === 'einzeln' && (
                          <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                            {p.kosten?fmtEur(parseFloat(p.kosten)):'–'}
                          </TableCell>
                        )}
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                {detailLV.kosten_modus === 'gesamt' && (
                  <Typography variant="caption" color="text.secondary">Pauschalabrechnung</Typography>
                )}
                <Box sx={{ bgcolor: '#4a7c59', color: 'white', borderRadius: 2, px: 2, py: 1, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>Gesamtkosten</Typography>
                  <Typography variant="h6" fontWeight={800}>{fmtEur(parseFloat(String(detailLV.gesamtkosten||0)))}</Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailOpen(false)}>Schließen</Button>
              <Button variant="outlined" startIcon={<Edit />} onClick={() => { setDetailOpen(false); handleEdit(detailLV); }}
                sx={{ borderColor: '#4a7c59', color: '#4a7c59' }}>Bearbeiten</Button>
              <Button variant="contained" startIcon={<PictureAsPdf />} onClick={() => exportPDF(detailLV)}
                sx={{ bgcolor: '#4a7c59' }}>PDF exportieren</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
